import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FILTER_OPTIONS } from "@/components/home/SearchFilter";
import { GoogleSignInButton, SignedInChip } from "@/components/GoogleAuth";
import { useVisitorAuth } from "@/context/VisitorAuthContext";

const inputClass =
  "w-full rounded-sm border border-gray-200 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/30";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-navy/60">
      {label} {required && <span className="text-gold-dark">*</span>}
    </span>
    {children}
  </label>
);

const EMPTY = {
  name: "",
  mobile: "",
  email: "",
  preferred_location: "",
  property_type: "",
  budget: "",
  plot_size: "",
  message: "",
};

export default function EnquiryForm() {
  const { visitor, loading } = useVisitorAuth();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visitor) {
      setForm((f) => ({
        ...f,
        name: f.name || visitor.name || "",
        email: f.email || visitor.email || "",
      }));
    }
  }, [visitor]);

  useEffect(() => {
    const property = sessionStorage.getItem("enquiry_property");
    if (property) {
      setForm((f) => ({
        ...f,
        property_title: property,
        message: `I'm interested in "${property}". Please share more details.`,
      }));
      sessionStorage.removeItem("enquiry_property");
    }
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry submitted. Our team will get in touch with you.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className="py-10 text-center text-sm font-semibold uppercase tracking-widest text-muted-foreground">Loading...</p>;
  }

  return (
    <div>
      {visitor ? (
        <SignedInChip />
      ) : (
        <div
          data-testid="optional-signin-banner"
          className="mb-5 flex flex-col items-center gap-3 rounded-sm border border-gold/40 bg-accent px-4 py-4 sm:flex-row sm:justify-between"
        >
          <p className="text-center text-xs font-semibold leading-relaxed text-navy/80 sm:text-left">
            Optional: sign in with Google for faster, verified follow-up.
          </p>
          <div className="w-full shrink-0 sm:w-60">
            <GoogleSignInButton label="Sign in with Google (Optional)" />
          </div>
        </div>
      )}
      <form data-testid="enquiry-form" onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" required>
          <input data-testid="enquiry-name-input" className={inputClass} value={form.name} onChange={set("name")} placeholder="Your full name" />
        </Field>
        <Field label="Mobile Number" required>
          <input data-testid="enquiry-mobile-input" className={inputClass} value={form.mobile} onChange={set("mobile")} placeholder="+91" type="tel" />
        </Field>
        <Field label="Email">
          <input data-testid="enquiry-email-input" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@example.com" type="email" />
        </Field>
        <Field label="Preferred Location">
          <select data-testid="enquiry-location-select" className={inputClass} value={form.preferred_location} onChange={set("preferred_location")}>
            <option value="">Select location</option>
            {FILTER_OPTIONS.location.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </Field>
        <Field label="Property Type">
          <select data-testid="enquiry-type-select" className={inputClass} value={form.property_type} onChange={set("property_type")}>
            <option value="">Select type</option>
            {FILTER_OPTIONS.type.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Approximate Budget">
          <select data-testid="enquiry-budget-select" className={inputClass} value={form.budget} onChange={set("budget")}>
            <option value="">Select budget</option>
            {FILTER_OPTIONS.budget.map((b) => (
              <option key={b.value} value={b.label}>{b.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Plot Size Requirement">
          <input data-testid="enquiry-plot-size-input" className={inputClass} value={form.plot_size} onChange={set("plot_size")} placeholder="e.g. 1,000 sq.ft." />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Message">
            <textarea
              data-testid="enquiry-message-input"
              className={`${inputClass} min-h-[110px] resize-y`}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us what you're looking for"
            />
          </Field>
        </div>
        <button
          data-testid="enquiry-submit-button"
          type="submit"
          disabled={submitting}
          className="rounded-sm bg-gold px-8 py-4 text-sm font-bold uppercase tracking-wider text-navy transition-colors duration-200 hover:bg-gold-light disabled:opacity-60 sm:col-span-2 sm:justify-self-start"
        >
          {submitting ? "Submitting..." : "Submit Enquiry"}
        </button>
      </form>
    </div>
  );
}
